/**
 * Часы сервера на клиенте.
 *
 * Дедлайн раунда приходит моментом времени по часам сервера, а остаток
 * считался от часов устройства. На компьютерах часы сверяются с интернетом
 * и точны до долей секунды, а телефон с выключенным «временем сети» может
 * спешить на минуты: при 30 секундах на раунд отсчёт сразу показывал 0:00.
 *
 * Смещение оценивается по заголовку Date ответов API. Он точен до секунды,
 * поэтому каждый ответ даёт не число, а промежуток, где смещение может
 * лежать: сервер поставил Date где-то между отправкой запроса и получением
 * ответа, а секунды в нём округлены вниз. Пересечение промежутков от
 * нескольких запросов сужает оценку до долей секунды.
 */

/** Секунда округления заголовка Date. */
const DATE_PRECISION_MS = 1000;

/** Границы смещения «сервер минус устройство», миллисекунды. */
let low = -Infinity;
let high = Infinity;

/**
 * Учитывает ответ сервера.
 *
 * @param dateHeader значение заголовка Date
 * @param sentAt     Date.now() перед отправкой запроса
 * @param receivedAt Date.now() после получения ответа
 */
export function observeServerDate(dateHeader: string | null, sentAt: number, receivedAt: number) {
  if (!dateHeader) return;
  const server = Date.parse(dateHeader);
  if (Number.isNaN(server) || receivedAt < sentAt) return;

  // Настоящее время сервера в момент ответа — от server до server + 1 с;
  // локальный момент ответа — где-то между sentAt и receivedAt.
  const sampleLow = server - receivedAt;
  const sampleHigh = server + DATE_PRECISION_MS - sentAt;

  const nextLow = Math.max(low, sampleLow);
  const nextHigh = Math.min(high, sampleHigh);
  if (nextLow <= nextHigh) {
    low = nextLow;
    high = nextHigh;
  } else {
    // Промежутки не пересекаются — часы устройства перевели (или их
    // подвела синхронизация). Прежние замеры больше не верны.
    low = sampleLow;
    high = sampleHigh;
  }
}

/** Насколько часы сервера впереди часов устройства, миллисекунды. */
export function serverOffset(): number {
  if (!Number.isFinite(low) || !Number.isFinite(high)) return 0;
  return (low + high) / 2;
}

/** Текущее время по часам сервера. */
export function serverNow(): number {
  return Date.now() + serverOffset();
}

/** Сбрасывает накопленную оценку. Нужен тестам. */
export function resetServerClock() {
  low = -Infinity;
  high = Infinity;
}
